// PatientFormDialog — modal compartido para crear y editar paciente.
//
// Sprint 3 B2: refactor desde mock de Figma Make. Alineado con schema real
// de `pacientes` (0001 + 0007). Diferencias vs el mock original:
//   - Eliminados campos que NO existen en schema: dni, diagnostico, estado,
//     kinesiologoId. Diagnóstico es del tratamiento, no del paciente.
//     "Estado" del dashboard se deriva en runtime (adherencia + frescura),
//     no es columna.
//   - Eliminado dropdown "Kinesiólogo asignado" — kine_id viene de auth.uid()
//     en la RPC, no se selecciona.
//   - body_type/altura_cm/peso_kg quedan fuera del form — el paciente los
//     declara en su propio perfil. Reduce fricción para el kine al cargar.
//   - fecha_nacimiento opcional al crear (paciente lo confirma en signup).
//     Editable en modo edit.
//
// Modos:
//   - "crear": campos mínimos para invitación (nombre, apellido, email,
//     telefono opcional, notas_clinicas opcional). onSave dispara RPC
//     crear_paciente_invitacion via useCrearPaciente.
//   - "editar": agrega fecha_nacimiento editable. onSave dispara UPDATE
//     directo via useEditarPaciente.

import { useEffect, useState, type FormEvent } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/app/components/ui/dialog";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { Textarea } from "@/app/components/ui/textarea";
import { Loader2 } from "lucide-react";

export interface PatientFormCreateData {
  nombre: string;
  apellido: string;
  email: string;
  telefono: string | null;
  notas_clinicas: string | null;
}

export interface PatientFormEditData extends PatientFormCreateData {
  fecha_nacimiento: string | null;
}

export interface PatientFormInitial {
  nombre: string;
  apellido: string;
  email: string;
  telefono: string | null;
  fecha_nacimiento: string | null;
  notas_clinicas: string | null;
}

interface PatientFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "crear" o "editar". Cambia el título + campos visibles + onSave shape. */
  mode: "crear" | "editar";
  /** Pre-fill para modo edit. null/undefined para modo crear. */
  initial?: PatientFormInitial | null;
  /** Submitting state desde el caller (mientras corre RPC/UPDATE). */
  submitting?: boolean;
  /** Error message desde el caller. */
  errorMessage?: string | null;
  /** Disparado al click "Crear paciente" / "Guardar cambios". */
  onSave: (data: PatientFormCreateData | PatientFormEditData) => void;
}

const EMPTY: PatientFormInitial = {
  nombre: "",
  apellido: "",
  email: "",
  telefono: null,
  fecha_nacimiento: null,
  notas_clinicas: null,
};

export function PatientFormDialog({
  open,
  onOpenChange,
  mode,
  initial,
  submitting = false,
  errorMessage = null,
  onSave,
}: PatientFormDialogProps) {
  const [form, setForm] = useState<PatientFormInitial>(EMPTY);
  const [localError, setLocalError] = useState<string | null>(null);

  // Reset form on open / initial change.
  useEffect(() => {
    setForm(initial ?? EMPTY);
    setLocalError(null);
  }, [initial, open]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!form.nombre.trim()) {
      setLocalError("Ingresá el nombre del paciente");
      return;
    }
    if (!form.apellido.trim()) {
      setLocalError("Ingresá el apellido del paciente");
      return;
    }
    if (!form.email.trim()) {
      setLocalError("Ingresá el email del paciente — necesario para la invitación");
      return;
    }
    // Validación email mínima — la RPC repite y formaliza.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setLocalError("El email tiene formato inválido");
      return;
    }

    const base: PatientFormCreateData = {
      nombre: form.nombre.trim(),
      apellido: form.apellido.trim(),
      email: form.email.trim().toLowerCase(),
      telefono: form.telefono?.trim() || null,
      notas_clinicas: form.notas_clinicas?.trim() || null,
    };

    if (mode === "editar") {
      const editData: PatientFormEditData = {
        ...base,
        fecha_nacimiento: form.fecha_nacimiento || null,
      };
      onSave(editData);
    } else {
      onSave(base);
    }
  };

  const set =
    <K extends keyof PatientFormInitial>(key: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }));

  const displayError = errorMessage ?? localError;

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === "editar" ? "Editar paciente" : "Nuevo paciente"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Datos personales */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mt-2 mb-3">
              Datos personales
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="nombre">Nombre *</Label>
                <Input
                  id="nombre"
                  value={form.nombre}
                  onChange={set("nombre")}
                  placeholder="Nombre"
                  disabled={submitting}
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="apellido">Apellido *</Label>
                <Input
                  id="apellido"
                  value={form.apellido}
                  onChange={set("apellido")}
                  placeholder="Apellido"
                  disabled={submitting}
                />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="paciente@email.com"
                  disabled={submitting}
                />
                {mode === "crear" && (
                  <p className="text-xs text-muted-foreground">
                    Le vamos a generar un link de invitación para activar su
                    cuenta.
                  </p>
                )}
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label htmlFor="telefono">
                  Teléfono{" "}
                  <span className="text-muted-foreground font-normal">
                    (opcional)
                  </span>
                </Label>
                <Input
                  id="telefono"
                  type="tel"
                  value={form.telefono ?? ""}
                  onChange={set("telefono")}
                  placeholder="+54 11 ..."
                  disabled={submitting}
                />
              </div>
              {mode === "editar" && (
                <div className="space-y-1.5 col-span-2">
                  <Label htmlFor="fecha_nacimiento">
                    Fecha de nacimiento{" "}
                    <span className="text-muted-foreground font-normal">
                      (opcional)
                    </span>
                  </Label>
                  <Input
                    id="fecha_nacimiento"
                    type="date"
                    value={form.fecha_nacimiento ?? ""}
                    onChange={set("fecha_nacimiento")}
                    disabled={submitting}
                    max={new Date().toISOString().slice(0, 10)}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Datos clínicos */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mt-4 mb-3">
              Datos clínicos
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="notas_clinicas">
                Notas clínicas{" "}
                <span className="text-muted-foreground font-normal">
                  (opcional)
                </span>
              </Label>
              <Textarea
                id="notas_clinicas"
                value={form.notas_clinicas ?? ""}
                onChange={set("notas_clinicas")}
                placeholder="Diagnóstico inicial, observaciones, historial relevante..."
                rows={3}
                disabled={submitting}
              />
              <p className="text-xs text-muted-foreground">
                El diagnóstico específico se carga en el tratamiento del paciente.
              </p>
            </div>
          </div>

          {displayError && (
            <div
              role="alert"
              className="text-sm rounded-md px-3 py-2 bg-red-50 text-red-900 border border-red-200"
            >
              {displayError}
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {mode === "editar" ? "Guardando…" : "Creando…"}
                </>
              ) : mode === "editar" ? (
                "Guardar cambios"
              ) : (
                "Crear paciente"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
